import { api } from '../../lib/api';
import type { PagedResult } from '../administration/users/userTypes';
import type {
  ApprovalCategoryDetail,
  ApprovalCategoryListItem,
  ApprovalListQuery,
  ApprovalRequestDetail,
  ApprovalRequestListItem,
  ApprovalWorkflowOption,
  SaveApprovalCategoryRequest,
} from './approvalTypes';
import type { ListQuery } from '../administration/users/userTypes';

function toQueryString(query: ApprovalListQuery): string {
  const params = new URLSearchParams();

  if (query.search?.trim()) params.set('search', query.search.trim());
  if (query.page && query.page > 1) params.set('page', String(query.page));
  if (query.pageSize) params.set('pageSize', String(query.pageSize));
  if (query.sort) params.set('sort', query.sort);

  /*
    Each facet is sent whenever the key is PRESENT, empty string included: an empty value is
    the reader saying "none of these", which matches no row. A truthiness check would drop it
    and the server would read the axis as unmentioned — the three-state facet rule.
  */
  if (query.status !== undefined) params.set('status', query.status);
  if (query.stage !== undefined) params.set('stage', query.stage);
  if (query.workflow !== undefined) params.set('workflow', query.workflow);

  // A view SCOPE rather than a facet: it changes what the list IS. Only sent when widened.
  if (query.scope === 'all') params.set('scope', 'all');

  const serialized = params.toString();
  return serialized ? `?${serialized}` : '';
}

function toCategoryQueryString(query: ListQuery): string {
  const params = new URLSearchParams();

  if (query.search?.trim()) params.set('search', query.search.trim());
  if (query.page && query.page > 1) params.set('page', String(query.page));
  if (query.pageSize) params.set('pageSize', String(query.pageSize));
  if (query.sort) params.set('sort', query.sort);
  if (query.status !== undefined) params.set('status', query.status);

  const serialized = params.toString();
  return serialized ? `?${serialized}` : '';
}

/**
 * The approval inbox and its decisions.
 *
 * **There is no `create`, and that absence is the engine's shape.** A request is submitted by
 * the owning module's own endpoint, behind that module's key — a generic one here would let a
 * caller put a record into an approval cycle its module refused.
 */
export const approvalsApi = {
  list: (query: ApprovalListQuery, signal?: AbortSignal) =>
    api<PagedResult<ApprovalRequestListItem>>(`/api/approvals${toQueryString(query)}`, { signal }),

  get: (id: string, signal?: AbortSignal) =>
    api<ApprovalRequestDetail>(`/api/approvals/${id}`, { signal }),

  /**
   * Signs this caller's active step. Refused when they have none. The remarks are OPTIONAL and
   * kept on the approver's own step; a caller that collects none sends no body at all.
   */
  approve: (id: string, remarks?: string | null) =>
    api<ApprovalRequestDetail>(
      `/api/approvals/${id}/approve`,
      remarks != null ? { method: 'POST', body: { remarks } } : { method: 'POST' },
    ),

  /** Satisfies an acknowledge-only step. There is no rejection counterpart for one. */
  acknowledge: (id: string) =>
    api<ApprovalRequestDetail>(`/api/approvals/${id}/acknowledge`, { method: 'POST' }),

  /** Refuses the request. One rejection ends it, whoever is still waiting. */
  reject: (id: string, reason: string) =>
    api<ApprovalRequestDetail>(`/api/approvals/${id}/reject`, {
      method: 'POST',
      body: { reason },
    }),

  /** The submitter pulls their own still-pending request back. */
  retract: (id: string) =>
    api<ApprovalRequestDetail>(`/api/approvals/${id}/retract`, { method: 'POST' }),
};

/**
 * Which approvers sign off on each workflow.
 *
 * **No `update`.** Configuring a workflow is idempotent on the workflow key, so the same call
 * serves a first configuration and a correction — see the controller for why that is the
 * honest shape rather than two endpoints reaching one row.
 */
export const approvalRoutingApi = {
  /** Every configurable workflow, derived from the registered handlers. */
  workflows: (signal?: AbortSignal) =>
    api<ApprovalWorkflowOption[]>('/api/approval-categories/workflows', { signal }),

  list: (query: ListQuery, signal?: AbortSignal) =>
    api<PagedResult<ApprovalCategoryListItem>>(
      `/api/approval-categories${toCategoryQueryString(query)}`,
      { signal },
    ),

  get: (id: string, signal?: AbortSignal) =>
    api<ApprovalCategoryDetail>(`/api/approval-categories/${id}`, { signal }),

  save: (body: SaveApprovalCategoryRequest) =>
    api<ApprovalCategoryDetail>('/api/approval-categories', { method: 'POST', body }),

  /** Returns the workflow to the administrator fallback. Requests in flight keep their chain. */
  remove: (id: string) => api<void>(`/api/approval-categories/${id}`, { method: 'DELETE' }),

  restore: (id: string) => api<void>(`/api/approval-categories/${id}/restore`, { method: 'POST' }),
};
